/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
import React, { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonProps, Dropdown } from 'react-bootstrap';

import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';
import { BASE_URL } from '../../core/constants/api';
import Table from '../../components/table/Table';
import Image from '../../components/image/Image';
import TH from '../../components/table/TH/TH';
import THead from '../../components/table/THead/THead';
import Button from '../../components/form/button/Button';
import Icon from '../../components/icon/Icon';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import { RootState } from '../../core/state/state';
import { getInternalUsers } from '../../core/state/users/asyncActions';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import { history } from '../../core/state/history';
import { InternalUserFilter } from '../../core/state/users/types';
import AssignUserRoleModal from './modals/assign-user-role/AssignUserRoleModal';
import { isReadAllRole } from '../../core/state/user/selectors';
import defaultUserImage from '../../../img/user-default.png';

import './InternalUsers.scss';

interface Props {
  pageType: number;
}

const InternalUsers: React.FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) => state.users.internalUsers);
  const [sortFilter, setSortFilter] = React.useState<InternalUserFilter>(InternalUserFilter.All);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const isUserReader = useSelector(isReadAllRole);

  const getUsers = (filter: InternalUserFilter) => {
    setSortFilter(filter);
    dispatch(getInternalUsers(filter));
  };

  React.useEffect(() => {
    getUsers(InternalUserFilter.All);
  }, []);

  const ToggleButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, onClick }, ref) => (
    <button
      ref={ref}
      disabled={!users}
      className="internal-users__filter-btn button button--outline"
      onClick={(e) => {
        e.preventDefault();
        if (onClick) {
          onClick(e);
        }
      }}
    >
      {children}
    </button>
  ));

  return (
    <TableWrapper>
      <div className="table__header">
        <span>{t('internalUsers.title')}</span>
        <div className="d-flex">
          <Dropdown alignRight className="internal-users__filter">
            <Dropdown.Toggle as={ToggleButton} id="filter-users">
              <span>{t('common.filterBy')}</span>
              <Icon className="internal-users__sort-icon" icon="arrow" size="extra-small" svg />
            </Dropdown.Toggle>
            <Dropdown.Menu className="internal-users__filter-menu" area-labelby="filter-users">
              <Dropdown.Item onClick={() => getUsers(InternalUserFilter.SystemAdmin)}>{t('internalUsers.filter.admin')}</Dropdown.Item>
              <Dropdown.Item onClick={() => getUsers(InternalUserFilter.ReadAll)}>{t('internalUsers.filter.reader')}</Dropdown.Item>
              <Dropdown.Item onClick={() => getUsers(InternalUserFilter.Empty)}>{t('internalUsers.filter.empty')}</Dropdown.Item>
              <Dropdown.Item onClick={() => getUsers(InternalUserFilter.All)}>{t('internalUsers.filter.all')}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {!isUserReader && (
            <Button disabled={!users} onClick={() => setIsModalOpen(true)} className="internal-users__sort" buttonTypes={['blue', 'small']}>
              <span>{t('common.assignRole')}</span>
            </Button>
          )}
        </div>
      </div>
      <div className="internal-users__table">
        <Table>
          <THead>
            <TR>
              <TH>{t('common.name')}</TH>
              <TH>{t('common.userRole')}</TH>
              <TH>{t('common.usernameEmail')}</TH>
              <TH>{t('common.phone')}</TH>
              <TH>{t('internalUsers.heading.landline')}</TH>
              <TH>{t('internalUsers.heading.position')}</TH>
              <TH>{t('internalUsers.heading.area')}</TH>
            </TR>
          </THead>
          <TBody>
            {users?.map((user, i) => (
              <TR className="internal-users__user" onClick={() => history.push(`/internal-users/${user.id}/projects`)} key={`${i}-${user.id}`}>
                <TD className="d-flex align-items-center">
                  <Image
                    className="internal-users__user-img"
                    src={`${BASE_URL}/${user.profilePhoto}`}
                    alt="user avatar"
                    onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
                      e.currentTarget.src = defaultUserImage;
                    }}
                  />
                  <span className="font-weight-bold">{`${user.firstName} ${user.lastName}`}</span>
                </TD>
                <TD className="text-left">{user.roleName === 'SystemUser' ? '-' : user.roleName}</TD>
                <TD className="text-left">{user.email}</TD>
                <TD className="text-left">{user.mobilePhoneNumber ?? '--'}</TD>
                <TD className="text-left">{user.phoneNumber}</TD>
                <TD className="text-left">{user.title}</TD>
                <TD className="text-left">{user.areaName}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {!users && (
          <NoResultsPage
            title={t('common.noUsers')}
            description={t('internalUsers.noResults.description')}
            icon="profile"
            iconClass="internal-users__no-results-icon"
          />
        )}
      </div>
      {isModalOpen && (
        <AssignUserRoleModal
          sortFilter={sortFilter}
          isOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
          getUsers={() => getUsers(InternalUserFilter.All)}
        />
      )}
    </TableWrapper>
  );
};

export default InternalUsers;
